import {
    DialogImportAttributesComponent,
    ImportAttributesResult
} from '../../dialogs/dialog-import-attributes/dialog-import-attributes.component';
import { MatDialog } from '@iota/iota-ui-components';
import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChildren
} from '@angular/core';
import { AttributesValueType, CompanyAttributeDTO } from '@iota/iota-management-portal-v4-client';

import { deepCopy } from '../../constants-and-enums/default-objects';
import { AttributeComponent } from '../attribute/attribute.component';
import { ATTRIBUTE_EXPORT_SEPARATOR, exportArrayToExcel, jsonStringifyArrayAndConcatenate } from '../../utils';

export interface AttributeListResult {
    upsertedAttributes: CompanyAttributeDTO[];
    removedAttributeIds: string[];
}

@Component({
    standalone: false,
    selector: 'app-attribute-list',
    templateUrl: './attribute-list.component.html',
    styleUrls: ['./attribute-list.component.scss']
})
export class AttributeListComponent implements OnInit, OnChanges {
    @ViewChildren('attributes') attributeChildren: QueryList<AttributeComponent>;

    get invalid(): boolean {
        return this.attributeComponents.some((att) => att.invalid);
    }

    constructor(
        private ref: ChangeDetectorRef,
        private dialog: MatDialog
    ) {}

    editMode = false;

    private attributesToDelete: string[] = [];

    attributes: CompanyAttributeDTO[] = [];

    @ViewChildren(AttributeComponent) attributeComponents!: QueryList<AttributeComponent>;

    @Input() parent: any;
    @Input() parentId: string;
    @Input() possibleAttributeKeys: string[] = [];
    showHelp = false;

    @Output() saveEvent = new EventEmitter<AttributeListResult>();

    ngOnInit(): void {
        this.resetAttributes();
    }
    ngOnChanges(changes: SimpleChanges) {
        const newParent = changes?.['parent']?.currentValue;

        if (newParent) {
            this.resetAttributes();
        }
    }
    add(): void {
        const newAttribute: CompanyAttributeDTO = {
            parentSkId: this.parentId,
            valueType: AttributesValueType.STRING
        };

        if (!this.attributes) {
            this.attributes = [];
        }

        this.attributes.unshift(newAttribute);

        this.ref.detectChanges();
    }

    delete(attribute: CompanyAttributeDTO): void {
        if (attribute.attributeId) {
            this.attributesToDelete = [...this.attributesToDelete, attribute.attributeId];
        }

        if (this.attributes.includes(attribute)) {
            this.attributes.splice(this.attributes.indexOf(attribute), 1);
        }
    }

    edit() {
        this.editMode = true;
    }

    cancel() {
        this.editMode = false;
        this.resetAttributes();
    }

    resetAttributes() {
        this.attributes = deepCopy(this.parent?.attributes);
        this.attributes.sort((a, b) => a.attributeName.localeCompare(b.attributeName));
    }

    save() {
        const upsertedAttributes = this.attributeChildren
            .toArray()
            .map((a) => a.getUpdatedAttribute())
            .filter((a) => !!a);

        this.editMode = false;

        this.saveEvent.emit({
            upsertedAttributes,
            removedAttributeIds: this.attributesToDelete
        });
    }

    importAttributes(): void {
        this.editMode = true;
        this.dialog
            .open(DialogImportAttributesComponent)
            .afterClosed()
            .subscribe((res: ImportAttributesResult) => {
                if (res && res.attributes) {
                    res.attributes.forEach((att) => (att.parentSkId = this.parentId));
                    if (res.deleteOldAttributes) {
                        this.attributesToDelete = this.attributes.filter((att) => att.attributeId).map((att) => att.attributeId);
                        this.attributes = res.attributes;
                    } else {
                        this.attributes = this.attributes.concat(res.attributes);
                    }
                    this.editMode = false;

                    this.saveEvent.emit({
                        upsertedAttributes: res.attributes,
                        removedAttributeIds: this.attributesToDelete
                    });
                    this.ref.detectChanges();
                }
            });
    }

    exportAttributes(): void {
        const attributeList = this.formatExportedAttributes();
        exportArrayToExcel(attributeList);
    }

    private formatExportedAttributes(): any[] {
        return this.attributes.map((obj) => {
            const exportObj = {};

            // it's better to only copy wanted properties, so new ones don't automatically get exported
            exportObj[`attributeName`] = obj.attributeName;
            exportObj[`valueType`] = obj.valueType;
            exportObj[`temporary`] = obj.temporary ? obj.temporary : false;
            exportObj[`booleanValue`] = jsonStringifyArrayAndConcatenate(obj.booleanValue, ATTRIBUTE_EXPORT_SEPARATOR);
            exportObj[`dateTimeValue`] = jsonStringifyArrayAndConcatenate(
                obj.dateTimeValue.map((date) => new Date(date)),
                ATTRIBUTE_EXPORT_SEPARATOR
            );
            exportObj[`dateValue`] = jsonStringifyArrayAndConcatenate(
                obj.dateValue.map((date) => new Date(date)),
                ATTRIBUTE_EXPORT_SEPARATOR
            );
            exportObj[`intValue`] = jsonStringifyArrayAndConcatenate(obj.intValue, ATTRIBUTE_EXPORT_SEPARATOR);
            exportObj[`numValue`] = jsonStringifyArrayAndConcatenate(obj.numValue, ATTRIBUTE_EXPORT_SEPARATOR);
            exportObj[`strValue`] = jsonStringifyArrayAndConcatenate(obj.strValue, ATTRIBUTE_EXPORT_SEPARATOR);
            exportObj[`uuidValue`] = jsonStringifyArrayAndConcatenate(obj.uuidValue, ATTRIBUTE_EXPORT_SEPARATOR);
            exportObj[`locale`] = obj.locale;

            return exportObj;
        });
    }
}
